import { useEffect, useId, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { AutosaveIndicator, type AutosaveState } from './AutosaveIndicator'

export type InlineTextType = 'text' | 'email' | 'url' | 'tel' | 'date'

/** How long "Opgeslagen" stays visible after a save. */
const SAVED_MS = 2000

/**
 * Click to edit; Enter or blur saves, Escape cancels (design §4.12, ED-52). An empty
 * value is sent as `null`; a `required` field keeps its old value instead.
 *
 * What was typed stays in view until the save succeeds. After a failed save that text
 * (the concept) stays, with the reason and "Niet opgeslagen – opnieuw proberen": the
 * link resends exactly the concept, and opening the field again starts from it, so it
 * can be corrected instead of retyped. Escape or an unchanged edit leaves the concept
 * as it is; typing the stored value back drops it.
 *
 * Enter and Escape put the focus back on the edit button, so keyboard and screen-reader
 * users stay where they were; leaving the field with Tab or a click keeps the focus
 * where it went. The autosave status stays mounted while editing, so its live region is
 * in place before each change.
 */
export function InlineText({
  value,
  onSave,
  label,
  placeholder = 'Toevoegen…',
  type = 'text',
  maxLength,
  required = false,
  className,
}: {
  value: string | null
  onSave: (value: string | null) => Promise<void>
  label: string
  placeholder?: string
  type?: InlineTextType
  maxLength?: number
  required?: boolean
  className?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [state, setState] = useState<AutosaveState>('idle')
  const [reason, setReason] = useState<string | null>(null)
  // The text of the newest save until it succeeds: shown while it saves, and kept as
  // the concept when it fails. null = nothing unsaved.
  const [unsaved, setUnsaved] = useState<string | null>(null)
  const done = useRef(false)
  // The text this edit started from: committing it unchanged changes nothing.
  const startText = useRef('')
  // Set by Enter and Escape, never by a blur: then the edit button takes the focus back.
  const refocus = useRef(false)
  const editButton = useRef<HTMLButtonElement>(null)
  const resetTimer = useRef<number | undefined>(undefined)
  // Bumped by every save and on unmount: only the newest save may report its outcome,
  // so a slow older one cannot overwrite it or start a timer after the field is gone.
  const newestSave = useRef(0)
  const valueId = useId()
  const shown = unsaved ?? value ?? ''

  useEffect(
    () => () => {
      newestSave.current += 1
      window.clearTimeout(resetTimer.current)
    },
    [],
  )

  useEffect(() => {
    if (editing || !refocus.current) return
    refocus.current = false
    editButton.current?.focus()
  }, [editing])

  function start() {
    // "Opgeslagen" belongs to the previous edit. A save that is still running and a
    // failure with its concept stay: their text is what this edit starts from.
    setState((current) => (current === 'saved' ? 'idle' : current))
    done.current = false
    startText.current = shown
    setDraft(shown)
    setEditing(true)
  }

  async function save(text: string) {
    newestSave.current += 1
    const mine = newestSave.current
    // The "Opgeslagen" timer of an earlier save must not hide this save's outcome.
    window.clearTimeout(resetTimer.current)
    setUnsaved(text)
    setState('saving')
    try {
      await onSave(text || null)
      if (mine !== newestSave.current) return
      setUnsaved(null)
      setState('saved')
      resetTimer.current = window.setTimeout(() => setState('idle'), SAVED_MS)
    } catch (caught) {
      if (mine !== newestSave.current) return
      setReason(caught instanceof Error ? caught.message : null)
      setState('error')
    }
  }

  function retry() {
    // The link only exists after a failure, when the field shows the concept: this
    // resends exactly that. The link disappears with the failure, so the focus moves
    // to the field instead of falling back to the page.
    editButton.current?.focus()
    void save(shown)
  }

  function commit() {
    if (done.current) return
    done.current = true
    setEditing(false)
    const clean = draft.trim()
    // Unchanged, or a required field emptied: the field shows what it showed before.
    if (clean === startText.current || (required && !clean)) return
    if (state === 'error' && clean === (value ?? '')) {
      // Back to the stored value after a failure: nothing to save, the concept goes.
      setUnsaved(null)
      setState('idle')
      return
    }
    void save(clean)
  }

  return (
    // A block that wraps: a long value is cut off with an ellipsis, and a long failure
    // message goes under the value instead of squeezing it.
    <span className={cn('flex min-w-0 flex-wrap items-center gap-x-2', className)}>
      {editing ? (
        <Input
          autoFocus
          aria-label={label}
          type={type}
          value={draft}
          maxLength={maxLength}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              refocus.current = true
              commit()
            } else if (event.key === 'Escape') {
              // Cancels this edit. It does not keep a surrounding Radix Dialog or Sheet
              // open: Radix listens for Escape on the document, before this handler, so
              // the overlay closes too. A screen that nests this field in an overlay has
              // to guard that overlay's onEscapeKeyDown itself.
              event.preventDefault()
              refocus.current = true
              done.current = true
              setEditing(false)
            }
          }}
          className="h-8"
        />
      ) : (
        <button
          ref={editButton}
          type="button"
          aria-label={`${label} bewerken`}
          aria-describedby={valueId}
          onClick={start}
          className={cn(
            'min-w-0 truncate rounded px-1 text-left text-sm hover:bg-muted',
            !shown && 'text-muted-foreground',
          )}
        >
          {/* The name replaces the visible text, so the value is read out as description. */}
          <span id={valueId}>{shown || placeholder}</span>
        </button>
      )}
      <AutosaveIndicator state={state} message={reason} onRetry={retry} />
    </span>
  )
}
